import { Injectable } from '@angular/core';
import { GitQuery } from '@y42/git';
import { JobStatus } from '@y42/json-schema';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { PushNotificationService } from '@y42/push-notification';
import { UserQuery } from '@y42/user/state';
import { catchError, combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';

export interface NotificationV2UserEventUserResourceData {
  service_type: 'SERVICE_TYPE_USER_RESOURCE';
  resource_type?: 'schema' | 'accounts';
}

export interface NotificationV2UserEventFileWriteData {
  service_type: 'SERVICE_TYPE_FILE_WRITE';
}

export interface NotificationV2UserEventJobData {
  service_type: 'SERVICE_TYPE_JOB';
  job_id?: string;
  status: JobStatus;
  import_status: JobStatus;
}

export interface NotificationV2UserEvent {
  org_id?: string;
  storage_type?: string;
  storage_id?: string;
  storage_branch?: string;
  location?: string;
  data?:
    | NotificationV2UserEventUserResourceData
    | NotificationV2UserEventFileWriteData
    | NotificationV2UserEventJobData;
}

@Injectable({ providedIn: 'root' })
export class AuthNotificationsService {
  constructor(
    private readonly pushNotificationService: PushNotificationService,
    private readonly userQuery: UserQuery,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly gitQuery: GitQuery,
  ) {}

  private readonly pushNotifications$ = combineLatest([this.userQuery.activeUser$, this.orgQuery.activeOrg$]).pipe(
    filter((vals) => vals.every((val) => val)),
    switchMap(() =>
      this.pushNotificationService.openChannel<NotificationV2UserEvent>(this.getChannelName()).pipe(
        map((evt) => evt.data),
        catchError((err: unknown) => {
          console.error(err);
          return of(null);
        }),
      ),
    ),
    filter(Boolean),
    filter((event) => {
      const activeBranchName = this.gitQuery.getActiveBranchName();
      const activeSpace = this.orgSpaceQuery.getActiveId();
      const activeOrgId = this.orgQuery.getActiveId();

      return (
        !!activeBranchName &&
        !!activeSpace &&
        !!activeOrgId &&
        event?.storage_branch === activeBranchName &&
        event?.storage_id === activeSpace &&
        `${event?.org_id}` === `${activeOrgId}`
      );
    }),
  );

  listen<T extends NotificationV2UserEvent = NotificationV2UserEvent>(
    predicate: (pushNotification: T) => boolean = () => true,
  ): Observable<T> {
    return (this.pushNotifications$ as Observable<T>).pipe(filter(predicate));
  }

  private getChannelName(): string {
    return `v2user.${this.userQuery.getActiveUserId()}`;
  }
}
